// ========== React & Libraries ==========
import { useMemo } from 'react';
import { z } from 'zod';

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import {
  CoachMissingGoogleMeet,
  GetCoachesMissingGoogleMeetSortableColumn,
  getCoachesMissingGoogleMeetSortableColumnSchema,
  getCoachesMissingGoogleMeetSortOrderSchema,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { EmptyArray, getCoachesMissingGoogleMeet } from '@/lib/api-calls';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { usePaginatedFilters } from '@/lib/filtering';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { airColumnHelper } from '@/lib/tables';
import { useSorting } from '@/lib/sorting';

// ========== Table Components ==========
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';

// ========== Layout Components ==========
import { InfoPage } from '@/components/info-page';

// ========== Users/Teachers Components ==========
import { coachesSectionTabs } from '../-components/tabs';
import { formatInstant, FrequentTimeZones, isAirAdmin } from '@airreading/common';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  sortBy: getCoachesMissingGoogleMeetSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/teachers/missing-google-meet/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: MissingGoogleMeetIndex,
  validateSearch: usersSearchSchema,
});

const columnHelper = airColumnHelper<CoachMissingGoogleMeet>(getCoachesMissingGoogleMeetSortableColumnSchema);

const coachesMissingGoogleMeetTableColumns = [
  columnHelper.accessor('fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/teachers/$teacherId'}
        params={{ teacherId: row.original.id }}
        data-testid={`teacher-missing-gm-link-name-${row.original.id}`}
      >
        {getValue()}
      </Link>
    ),
  }),

  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  columnHelper.accessor((row) => row.nextGoogleMeetSession, {
    id: 'nextGoogleMeetSessionDate',
    header: 'Next GM session date',
    cell: ({ getValue }) => {
      const session = getValue();
      return session && formatInstant(session.startInstant, FrequentTimeZones.UsCentral).formattedDate;
    },
  }),

  columnHelper.accessor((row) => row.nextGoogleMeetSession, {
    id: 'schoolDistrict',
    header: 'School - District',
    cell: ({ getValue }) => {
      const session = getValue();
      return session ? `${session.schoolName} - ${session.districtName}` : '';
    },
  }),
];

function MissingGoogleMeetIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachesMissingGoogleMeetSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const { data, isPending, error, refetch } = useQuery(
    getCoachesMissingGoogleMeet({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
      },
      sortBy,
    }),
  );
  const coaches = useMemo(() => data?.coaches || EmptyArray, [data]);

  useErrorToast(error, 'Error loading teachers');

  const table = useReactTable({
    data: coaches,
    columns: coachesMissingGoogleMeetTableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterComponents = (
    <>
      <TableFilterInputField
        data-testid="teachers-missing-gm-filter-input-search"
        placeholder="Search name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterResetButton
        data-testid="teachers-missing-gm-filter-button-reset"
        show={true}
        onClick={resetFilters}
      />
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No teachers missing Google Meet link found"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
      />
    </InfoPage>
  );
}
